import { supabase } from '@/lib/supabase'
import type {
  AllocationTableRow,
  AllocationFormData,
  AllocationFilters,
  PositionType,
  AllocationStatus,
  PresenceStatus,
} from '@/types/allocations'

/**
 * Fetch all allocations with optional filtering
 * Includes joins to aircraft, areas, units, locations
 */
export async function fetchAllocations(
  organizationId: string,
  filters?: AllocationFilters
): Promise<AllocationTableRow[]> {
  let query = supabase
    .from('allocations')
    .select(`
      id,
      organization_id,
      location_id,
      aircraft_id,
      area_id,
      unit_id,
      zone_id,
      position_type,
      position_x,
      position_y,
      position_z,
      rotation,
      custom_label,
      show_label,
      color_override,
      status,
      presence,
      starts_at,
      ends_at,
      expected_return_at,
      has_collision,
      collision_details,
      lease_contract_id,
      transient_stay_id,
      created_at,
      created_by,
      updated_at,
      updated_by,
      deleted_at,
      deleted_by,
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          model,
          manufacturer
        )
      ),
      location:location_id (
        name
      ),
      area:area_id (
        name
      ),
      unit:unit_id (
        unit_code
      )
    `)
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  // Apply filters
  if (filters?.status && filters.status.length > 0) {
    query = query.in('status', filters.status)
  }

  if (filters?.positionType && filters.positionType.length > 0) {
    query = query.in('position_type', filters.positionType)
  }

  if (filters?.areaId) {
    query = query.eq('area_id', filters.areaId)
  }

  if (filters?.locationId) {
    query = query.eq('location_id', filters.locationId)
  }

  if (filters?.withCollisions) {
    query = query.eq('has_collision', true)
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase()
    query = query.or(`custom_label.ilike.%${search}%`)
  }

  const { data, error } = await query

  if (error) throw error

  // Transform joined data to flat structure
  return (data ?? []).map((row): AllocationTableRow => {
    const aircraft = (row.aircraft as unknown as {
      tail_number: string
      aircraft_types: { model: string; manufacturer: string } | null
    } | null)
    const location = (row.location as unknown as { name: string } | null)
    const area = (row.area as unknown as { name: string } | null)
    const unit = (row.unit as unknown as { unit_code: string } | null)

    return {
      id: row.id,
      organization_id: row.organization_id,
      location_id: row.location_id,
      aircraft_id: row.aircraft_id,
      tail_number: aircraft?.tail_number ?? null,
      aircraft_type_name: aircraft?.aircraft_types
        ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`.trim()
        : null,
      location_name: location?.name ?? null,
      area_id: row.area_id,
      area_name: area?.name ?? null,
      unit_id: row.unit_id,
      unit_code: unit?.unit_code ?? null,
      zone_id: row.zone_id,
      position_type: row.position_type as PositionType,
      position_x: row.position_x,
      position_y: row.position_y,
      position_z: row.position_z,
      rotation: row.rotation,
      custom_label: row.custom_label,
      show_label: row.show_label,
      color_override: row.color_override,
      status: row.status as AllocationStatus,
      presence: row.presence as PresenceStatus,
      starts_at: row.starts_at ?? null,
      ends_at: row.ends_at ?? null,
      expected_return_at: row.expected_return_at ?? null,
      has_collision: row.has_collision,
      collision_details: row.collision_details,
      lease_contract_id: row.lease_contract_id,
      transient_stay_id: row.transient_stay_id,
      created_at: row.created_at,
      created_by: row.created_by,
      updated_at: row.updated_at,
      updated_by: row.updated_by,
      deleted_at: row.deleted_at,
      deleted_by: row.deleted_by,
    }
  })
}

/**
 * Fetch a single allocation by ID with full details
 */
export async function fetchAllocationById(id: string): Promise<AllocationTableRow> {
  const { data, error } = await supabase
    .from('allocations')
    .select(`
      id,
      organization_id,
      location_id,
      aircraft_id,
      area_id,
      unit_id,
      zone_id,
      position_type,
      position_x,
      position_y,
      position_z,
      rotation,
      custom_label,
      show_label,
      color_override,
      status,
      presence,
      starts_at,
      ends_at,
      expected_return_at,
      has_collision,
      collision_details,
      lease_contract_id,
      transient_stay_id,
      created_at,
      created_by,
      updated_at,
      updated_by,
      deleted_at,
      deleted_by,
      aircraft:aircraft_id (
        tail_number,
        aircraft_types:aircraft_type_id (
          model,
          manufacturer
        )
      ),
      location:location_id (
        name
      ),
      area:area_id (
        name
      ),
      unit:unit_id (
        unit_code
      )
    `)
    .eq('id', id)
    .single()

  if (error) throw error

  const aircraft = (data.aircraft as unknown as {
    tail_number: string
    aircraft_types: { model: string; manufacturer: string } | null
  } | null)
  const location = (data.location as unknown as { name: string } | null)
  const area = (data.area as unknown as { name: string } | null)
  const unit = (data.unit as unknown as { unit_code: string } | null)

  return {
    id: data.id,
    organization_id: data.organization_id,
    location_id: data.location_id,
    aircraft_id: data.aircraft_id,
    tail_number: aircraft?.tail_number ?? null,
    aircraft_type_name: aircraft?.aircraft_types
      ? `${aircraft.aircraft_types.manufacturer} ${aircraft.aircraft_types.model}`.trim()
      : null,
    location_name: location?.name ?? null,
    area_id: data.area_id,
    area_name: area?.name ?? null,
    unit_id: data.unit_id,
    unit_code: unit?.unit_code ?? null,
    zone_id: data.zone_id,
    position_type: data.position_type as PositionType,
    position_x: data.position_x,
    position_y: data.position_y,
    position_z: data.position_z,
    rotation: data.rotation,
    custom_label: data.custom_label,
    show_label: data.show_label,
    color_override: data.color_override,
    status: data.status as AllocationStatus,
    presence: data.presence as PresenceStatus,
    starts_at: data.starts_at ?? null,
    ends_at: data.ends_at ?? null,
    expected_return_at: data.expected_return_at ?? null,
    has_collision: data.has_collision,
    collision_details: data.collision_details,
    lease_contract_id: data.lease_contract_id,
    transient_stay_id: data.transient_stay_id,
    created_at: data.created_at,
    created_by: data.created_by,
    updated_at: data.updated_at,
    updated_by: data.updated_by,
    deleted_at: data.deleted_at,
    deleted_by: data.deleted_by,
  }
}

/**
 * Create a new allocation
 */
export async function createAllocation(
  organizationId: string,
  data: AllocationFormData
): Promise<AllocationTableRow> {
  const { data: created, error } = await supabase
    .from('allocations')
    .insert({
      organization_id: organizationId,
      aircraft_id: data.aircraft_id,
      location_id: data.location_id,
      area_id: data.area_id ?? null,
      unit_id: data.unit_id ?? null,
      zone_id: data.zone_id ?? null,
      position_type: data.position_type,
      position_x: data.position_x ?? 0,
      position_y: data.position_y ?? 0,
      position_z: data.position_z ?? 0,
      rotation: data.rotation ?? 0,
      custom_label: data.custom_label ?? null,
      show_label: data.show_label ?? false,
      color_override: data.color_override ?? null,
      status: data.status ?? 'active',
      presence: data.presence ?? 'present',
      starts_at: data.starts_at ?? new Date().toISOString(),
      ends_at: data.ends_at ?? null,
      lease_contract_id: data.lease_contract_id ?? null,
      transient_stay_id: data.transient_stay_id ?? null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)
    .select()
    .single()

  if (error) throw error

  // Fetch with full joins for return
  return fetchAllocationById(created.id)
}

/**
 * Update an existing allocation
 */
export async function updateAllocation(
  id: string,
  data: Partial<AllocationFormData>
): Promise<AllocationTableRow> {
  const updateData: Record<string, unknown> = {}

  // Only include fields that are explicitly provided
  if (data.aircraft_id !== undefined) updateData.aircraft_id = data.aircraft_id
  if (data.location_id !== undefined) updateData.location_id = data.location_id
  if (data.area_id !== undefined) updateData.area_id = data.area_id
  if (data.unit_id !== undefined) updateData.unit_id = data.unit_id
  if (data.zone_id !== undefined) updateData.zone_id = data.zone_id
  if (data.position_type !== undefined) updateData.position_type = data.position_type
  if (data.position_x !== undefined) updateData.position_x = data.position_x
  if (data.position_y !== undefined) updateData.position_y = data.position_y
  if (data.position_z !== undefined) updateData.position_z = data.position_z
  if (data.rotation !== undefined) updateData.rotation = data.rotation
  if (data.custom_label !== undefined) updateData.custom_label = data.custom_label
  if (data.show_label !== undefined) updateData.show_label = data.show_label
  if (data.color_override !== undefined) updateData.color_override = data.color_override
  if (data.status !== undefined) updateData.status = data.status
  if (data.presence !== undefined) updateData.presence = data.presence
  if (data.starts_at !== undefined) updateData.starts_at = data.starts_at
  if (data.ends_at !== undefined) updateData.ends_at = data.ends_at
  if (data.lease_contract_id !== undefined) updateData.lease_contract_id = data.lease_contract_id
  if (data.transient_stay_id !== undefined) updateData.transient_stay_id = data.transient_stay_id

  const { error } = await supabase.from('allocations').update(updateData).eq('id', id)

  if (error) throw error

  // Fetch with full joins for return
  return fetchAllocationById(id)
}

/**
 * Soft delete an allocation
 */
export async function deleteAllocation(id: string): Promise<void> {
  const { error } = await supabase
    .from('allocations')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// ============================================================================
// HANGAR VIEW - Position/Rotation Updates
// ============================================================================

/**
 * Update allocation position and rotation (for ramp view drag/drop)
 * Includes auto-detected area and unit assignment with position type
 */
export async function updateAllocationPosition(
  allocationId: string,
  position: [number, number, number],
  rotation: number,
  areaId: string | null = null,
  unitId: string | null = null,
  positionType: PositionType = 'unknown'
): Promise<void> {
  const { error } = await supabase
    .from('allocations')
    .update({
      position_x: position[0],
      position_y: position[1],
      position_z: position[2],
      rotation: rotation,
      area_id: areaId,
      unit_id: unitId,
      position_type: positionType,
      status: 'active',
      presence: 'present',
    })
    .eq('id', allocationId)

  if (error) throw error
}

/**
 * Get the current saved position for an allocation
 * Used to revert to saved state
 */
export async function getAllocationPosition(
  allocationId: string
): Promise<{
  position: [number, number, number]
  rotation: number
} | null> {
  const { data, error } = await supabase
    .from('allocations')
    .select('position_x, position_y, position_z, rotation')
    .eq('id', allocationId)
    .single()

  if (error) {
    console.error('Error fetching allocation position:', error)
    return null
  }

  return {
    position: [
      Number(data.position_x) || 0,
      Number(data.position_y) || 0,
      Number(data.position_z) || 0,
    ],
    rotation: Number(data.rotation) || 0,
  }
}
